import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TimeAgo from 'timeago-react';
import _ from 'lodash';

import { useToggle } from 'javascripts/hooks/use-toggle';
import { stringFormatUtils } from 'javascripts/shared/utils/string_format_utils';
import { bubEvents } from 'javascripts/shared/events/bub_events';
import { userData } from 'javascripts/shared/utils/user_data';

import { ChirpButton } from 'javascripts/components/chirp_button';
import { StarRating } from 'javascripts/components/star_rating';
import { ReportReviewModal } from 'javascripts/components/reviews/report_review_modal';
import { ReviewLikesSection } from 'javascripts/components/reviews/review_likes_section';

import styles from 'stylesheets/components/reviews/review_card.module';

const COMPACT_TEXT_CHAR_MAX = 500;

export function ReviewCard(props) {
  const [hideFullText, toggleHideFullText] = useToggle(
    stringFormatUtils.willTruncate(props.review.review, COMPACT_TEXT_CHAR_MAX),
  );
  const [reportReviewModalOpen, setReportReviewModalOpen] = useState(false);
  const [showAlreadySubmittedText, setShowAlreadySubmittedText] = useState(false);

  function renderRating() {
    if (!props.review.rating) {
      return null;
    }
    return <StarRating rating={props.review.rating} />;
  }

  function renderHeader() {
    if (!props.review.rating && !props.review.isCurrentUserReview) {
      return null;
    }
    return (
      <div className={styles.header}>
        {renderRating()}
        {props.review.isCurrentUserReview ? (
          <ChirpButton
            class={styles.editLink}
            displayStyle='underline'
            displayType='link'
            fontSize='small'
            label='Edit Review'
            onClick={props.onEditReviewClick}
          />
        ) : null}
      </div>
    );
  }

  function renderMoreText(compactText) {
    if (!hideFullText) return null;
    const moreText = props.review.review.substr(compactText.length);
    return moreText;
  }

  function renderText() {
    const reviewText = stringFormatUtils.format(props.review.review, hideFullText, COMPACT_TEXT_CHAR_MAX, false);
    const showMoreText = !hideFullText ? null : (
      <span>
        ...
        <span className={styles.hiddenText}>{renderMoreText(reviewText)}</span>
        &nbsp;
        <a onClick={toggleHideFullText} className={styles.readMoreLink}>
          read more
        </a>
      </span>
    );
    return (
      <div className={styles.text}>
        {reviewText}
        {showMoreText}
      </div>
    );
  }

  function onReportSubmitted() {
    setShowAlreadySubmittedText(true);
  }

  function renderReportReviewModal() {
    if (!reportReviewModalOpen) {
      return null;
    }

    return (
      <ReportReviewModal
        appSelector='#reviews-app'
        isOpen={reportReviewModalOpen}
        onRequestClose={() => setReportReviewModalOpen(false)}
        onReportSubmitted={onReportSubmitted}
        reviewId={props.review.id}
      />
    );
  }

  function onClickReportReview() {
    setReportReviewModalOpen(true);
    bubEvents.track('Review', 'Clicked flag review', {
      label: 'Review id',
      value: props.review.id,
    });
  }

  return (
    <div className={styles.reviewCard}>
      {renderHeader()}
      {renderText()}
      <div className={styles.footer}>
        {props.review.userDisplayName}
        <span className={styles.footerSeparator}>|</span>
        <TimeAgo datetime={props.review.createdAt} locale='en' live={false} />
        <span className={styles.footerSeparator}>|</span>
        Verified Purchase
      </div>
      <ReviewLikesSection
        likedByCurrentUser={props.review.likedByCurrentUser}
        likesCount={props.review.likesCount}
        userReviewId={props.review.id}
        showButton={userData.isLoggedIn() && !props.review.isCurrentUserReview}
      />

      {showAlreadySubmittedText ?
        <div className={styles.reviewReportedText}>
          You&apos;ve already flagged this review. Our Support team will look at this review to make sure it adheres to our content guidelines.
        </div> :
        <div className={styles.reportReviewLink}>
          <ChirpButton
            displayStyle='lightGray'
            displayType='link'
            fontSize='extraSmall'
            label='Report a problem with this review'
            onClick={onClickReportReview}
          />
        </div>
      }

      {renderReportReviewModal()}
    </div>
  );
}

ReviewCard.propTypes = {
  review: PropTypes.shape({
    createdAt: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    isCurrentUserReview: PropTypes.bool.isRequired,
    likedByCurrentUser: PropTypes.bool,
    likesCount: PropTypes.number,
    review: PropTypes.string.isRequired,
    userDisplayName: PropTypes.string.isRequired,
    rating: PropTypes.number,
  }).isRequired,
  onEditReviewClick: PropTypes.func,
};

ReviewCard.defaultProps = {
  onEditReviewClick: _.noop,
};
